import React, { useState, useRef, useEffect } from "react";
import "react-quill/dist/quill.snow.css";
import "../../assets/css/quill.css";
import { QuillFormats } from "../../constant";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useParams } from "react-router-dom";
import { customAlphabet } from "nanoid";
import { useGetHostEventByIdQuery } from "../../redux/api/eventApi";
import { setUpdateStatus } from "../../features/eventSlice";
import { Link } from "react-router-dom";
import Editor from "react-simple-wysiwyg";

interface User {
  id: string;
}
interface SelectedImage {
  file: File;
  preview: string;
}

interface TicketCategory {
  name: string;
  price: string;
  discountPrice: string;
  walletDiscount: string;
  qty: string;
  numOfPeople: string;
}

interface Category {
  name: string;
  price: string;
  discountPrice: string;
  walletDiscount: string;
  qty: string;
  numOfPeople: string;
}

type CurrencySymbolMap = {
  [key: string]: string;
  GBP: "£";
  USD: "$";
  NGN: "₦";
  EUR: "€";
  // Add more currencies if needed
};

const currencySymbolMap: CurrencySymbolMap = {
  GBP: "£",
  USD: "$",
  NGN: "₦",
  EUR: "€",
  // Add more currencies if needed
};

interface StartInfo {
  date: string;
  time: string;
}

interface EventData {
  eventTitle: string;
  venue: string;
  selectedImages?: File[]; // Assuming this is a file array

  startTime: string;
  endTime: string;
  startDate: string;
  endDate: string;
  description: string;
  ticketCategories: Category[];
  chargesBearer: string;
  currency: string;
  eventType: string;
  banner: File[];
  start: StartInfo[];
  end: StartInfo[];
}

const EditEventForm: React.FC = () => {
  const { sn } = useParams<{ sn: string }>();
  const formats = QuillFormats;
  const [step, setStep] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [showInformation, setShowInformation] = useState(false);
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);

  const dispatch = useDispatch();

  const user = useSelector(
    (state: RootState) => state.auth.user
  ) as User | null;
  const hostid = user?.id || "";

  const { data: events } = useGetHostEventByIdQuery(user?.id!, {
    skip: !user?.id, // Skip the query if user ID is not available
  });

  const [eventData, setEventData] = useState<EventData>({
    eventTitle: "",
    venue: "",
    startTime: "",
    endTime: "",
    startDate: "",
    endDate: "",
    description: "",
    ticketCategories: [
      {
        name: "Regular",
        price: "200",
        discountPrice: "0",
        walletDiscount: "0",
        qty: "10",
        numOfPeople: "10",
      },
    ],
    chargesBearer: "client",
    currency: "",
    eventType: "",
    banner: [],
    start: [
      {
        date: "",
        time: "",
      },
    ],
    end: [
      {
        date: "",
        time: "",
      },
    ],
  });

  useEffect(() => {
    if (events && events.data) {
      const event = events.data.find((e) => e.sn === sn);
      if (event) {
        setEventData({
          eventTitle: event.title,
          venue: event.venue,
          startTime: `${event.from_time}`,
          endTime: `${event.to_time}`,
          startDate: event.from_date,
          endDate: event.to_date,
          description: event.des,
          ticketCategories: event.ticketCategories.map((category) => ({
            name: category.name,
            price: category.price,
            discountPrice: "0",
            walletDiscount: "0",
            qty: category.quantity,
            numOfPeople: category.noofpeople,
          })),
          chargesBearer: event.paystack_bearer,
          currency: event.currency,
          eventType: event.event_cat,
          banner: [],
          start: [{ date: event.from_date, time: event.from_time }],
          end: [{ date: event.to_date, time: event.to_time }],
        });

        const fetchedImages = event.imgs
          ? event.imgs.map(
              (img) => `${process.env.REACT_APP_IMAGEURL}/${img.img}`
            )
          : [];
        setExistingImages(fetchedImages);
      }
    }
  }, [events, sn]);

  useEffect(() => {
    return () => {
      selectedImages.forEach((image) => URL.revokeObjectURL(image.preview));
    };
  }, [selectedImages]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEventData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleNextStep = () => {
    if (step < 4) {
      setStep(step + 1);
    }
  };

  const handlePreviousStep = () => {
    setStep(step - 1);
  };

  const handleDescriptionChange = (e: { target: { value: string } }) => {
    setEventData((prev) => ({
      ...prev,
      description: e.target.value,
    }));
  };

  const handleAddCategory = () => {
    const newCategory: Category = {
      name: "",
      price: "",
      discountPrice: "0",
      walletDiscount: "0",
      qty: "",
      numOfPeople: "",
    };

    setEventData((prevData) => ({
      ...prevData,
      ticketCategories: [...prevData.ticketCategories, newCategory],
    }));
  };

  const handleCategoryChange = (
    index: number,
    key: keyof TicketCategory,
    value: string | number
  ) => {
    const newCategories = [...eventData.ticketCategories];
    newCategories[index] = { ...newCategories[index], [key]: value };
    setEventData({ ...eventData, ticketCategories: newCategories });
  };

  const handleCurrencyChange = (currency: string) => {
    setEventData({ ...eventData, currency });
  };

  const handleDeleteCategory = (index: number) => {
    const updatedCategories = [...eventData.ticketCategories];
    updatedCategories.splice(index, 1);
    setEventData({ ...eventData, ticketCategories: updatedCategories });
  };

  const ampmOptions = ["AM", "PM"];

  const [startAmPm, setStartAmPm] = useState<string>("AM");
  const [endAmPm, setEndAmPm] = useState<string>("AM");

  const handleStartAmPmChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStartAmPm(e.target.value);
  };

  const handleEndAmPmChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEndAmPm(e.target.value);
  };

  const handleStartDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newStart = [...eventData.start];
    newStart[index].date = e.target.value;
    setEventData({ ...eventData, start: newStart });
  };

  const handleEndDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newEnd = [...eventData.end];
    newEnd[index].date = e.target.value;
    setEventData({ ...eventData, end: newEnd });
  };

  const handleStartTimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newStart = [...eventData.start];
    newStart[index].time = e.target.value;
    setEventData({ ...eventData, start: newStart });
  };

  const handleEndTimeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newEnd = [...eventData.end];
    newEnd[index].time = e.target.value;
    setEventData({ ...eventData, end: newEnd });
  };

  const handleImageDelete = (index: number, isExisting: boolean) => {
    if (isExisting) {
      // Mark existing image for deletion on backend
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
    } else {
      // Remove from new images selection
      setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleEventTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setEventData({ ...eventData, eventType: value });
  };
  // Function to convert base64 string to Blob
  const base64ToBlob = (base64String: string): Blob => {
    const byteString = atob(base64String.split(",")[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }
    return new Blob([arrayBuffer], { type: "image/png" }); // Adjust the type accordingly
  };

  const handleNewImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const selectedFiles = Array.from(files);

      // Check total images won't exceed 5 (existing + new selections)
      const totalImages =
        selectedFiles.length + selectedImages.length + existingImages.length;

      if (totalImages > 5) {
        toast.error(`You can't select more than five images total`);
        return;
      }

      // Create new image objects with previews
      const newImages = selectedFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }));

      setSelectedImages((prev) => [...prev, ...newImages]);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionError("");

    try {
      // First, handle the image updates
      if (selectedImages.length > 0) {
        const imageFormData = new FormData();
        imageFormData.append("hostid", hostid);
        imageFormData.append("eventid", sn || "");

        const nanoid = customAlphabet("123456789", 11);

        for (const image of selectedImages) {
          const elevenDigitName = nanoid();
          const fileExtension = image.file.name.split(".").pop();
          const renamedFile = new File(
            [image.file],
            `${elevenDigitName}.${fileExtension}`,
            { type: image.file.type }
          );
          imageFormData.append("images[]", renamedFile);
        }

        const imageResponse = await fetch(
          `${process.env.REACT_APP_BASEURL}/eventhost/addpics`,
          {
            method: "POST",
            body: imageFormData,
          }
        );
        console.log(imageResponse);
        // Add proper error handling
        if (!imageResponse.ok) {
          const errorText = await imageResponse.text();
          try {
            const errorData = JSON.parse(errorText);
            throw new Error(
              errorData.message || "Failed to update event images"
            );
          } catch {
            throw new Error(
              `Image upload failed with status ${imageResponse.status}`
            );
          }
        }
      }

      // Then handle the rest of the form data
      const formData = new FormData();
      formData.append("hostid", hostid);

      // Include existing images that haven't been deleted
      existingImages.forEach((imageUrl) => {
        formData.append("existingImages[]", imageUrl);
      });

      // Process other form data
      Object.entries(eventData).forEach(([key, value]) => {
        if (key === "ticketCategories") {
          value.forEach((category: any, index: number) => {
            Object.entries(category).forEach(([subKey, subValue]) => {
              formData.append(
                `ticketCategories[${index}][${subKey}]`,
                String(subValue)
              );
            });
          });
        } else if (Array.isArray(value)) {
          value.forEach((item: any, index: number) => {
            Object.entries(item).forEach(([subKey, subValue]) => {
              formData.append(`${key}[${index}][${subKey}]`, String(subValue));
            });
          });
        } else if (key !== "selectedImages" && key !== "banner") {
          formData.append(key, String(value));
        }
      });

      // Submit main form data
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/update/eventticket/${sn}`,
        {
          method: "POST",
          body: formData,
        }
      );

      // Add proper response handling
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error(
          `Invalid JSON response: ${responseText.substring(0, 100)}`
        );
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update event");
      }

      if (data.error === false) {
        toast.success(data.message);
        dispatch(setUpdateStatus(true));
        setSelectedImages([]);
      } else {
        throw new Error(data.message || "Unknown error");
      }
    } catch (error: any) {
      console.error("Submission error:", error);
      setSubmissionError(error.message || "Error updating event");
      toast.error(error.message || "Error updating event");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result && typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to read file as data URL."));
        }
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleInformationClick = () => {
    setShowInformation((prevShowInformation) => !prevShowInformation);
  };

  return (
    <div className="flex items-center justify-center">
      <div className="bg-gray-300/10 p-8 rounded shadow-lg w-4/5 lg:w-3/5 mt-24">
        <h2 className="text-2xl font-semibold mb-4 text-gray-900">
          Mettre à jour l'événement
        </h2>
        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <>
              <div className="mb-4">
                <label htmlFor="title" className="block text-gray-600 mb-2">
                  Titre de l'événement
                </label>
                <input
                  type="text"
                  id="eventTitle"
                  name="eventTitle"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                  value={eventData.eventTitle}
                  onChange={handleChange}
                  required
                  placeholder="Da Ministry reunion"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="venue" className="block text-gray-600 mb-2">
                  Lieu de l'événement
                </label>
                <input
                  type="text"
                  id="venue"
                  name="venue"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                  value={eventData.venue}
                  onChange={handleChange}
                  // placeholder="London, England"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="banner" className="block text-gray-600">
                  Event Images (up to 5)
                </label>
                <div className="border border-dashed p-12 rounded-md">
                  <div className="flex items-center">
                    <label
                      htmlFor="banner"
                      className="cursor-pointer bg-[#25aae1] text-white px-4 py-2 rounded-md mr-2 focus:outline-none"
                    >
                      Add Event Image
                    </label>
                    <input
                      type="file"
                      id="banner"
                      name="banner"
                      accept="image/*"
                      className="hidden"
                      onChange={handleNewImageChange}
                      ref={fileInputRef}
                      multiple
                    />
                    {/* Information button remains the same */}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap">
                  {/* Display existing images */}
                  {existingImages.map((image, index) => (
                    <div
                      key={`existing-${index}`}
                      className="mr-2 mb-2 relative"
                    >
                      <img
                        src={image}
                        alt={`existing-${index}`}
                        className="h-24 w-24 object-cover border rounded-md"
                      />
                      <button
                        type="button"
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                        onClick={() => handleImageDelete(index, true)}
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  {/* Display newly selected images */}
                  {selectedImages.map((image, index) => (
                    <div key={`new-${index}`} className="mr-2 mb-2 relative">
                      <img
                        src={image.preview}
                        alt={`new-${index}`}
                        className="h-24 w-24 object-cover border rounded-md"
                      />
                      <button
                        type="button"
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                        onClick={() => handleImageDelete(index, false)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add information tooltip or modal */}
              {showInformation && (
                <div className="bg-white p-4 border rounded-md">
                  <p className=" italic text-blue-400">
                    Les graphiques de l'événement doivent de préférence inclure
                    les dimensions (220 x 330 px) et (500 x 550 px), mais toute
                    taille fournie peut être redimensionnée pour s'adapter. Les
                    formats pris en charge sont jpg, jpeg, png, gif.{" "}
                  </p>
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="mb-4">
                <label htmlFor="startDate" className="block text-gray-600 mb-2">
                  Date de début
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                  value={eventData.start[0].date} // Assuming you want to bind to the first element of the start array
                  onChange={(e) => handleStartDateChange(e, 0)} // Pass index to handle function if needed
                  required
                />
              </div>
              <div className="mb-4">
                <label htmlFor="endDate" className="block text-gray-600 mb-2">
                  Date de fin
                </label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                  value={eventData.end[0].date} // Assuming you want to bind to the first element of the end array
                  onChange={(e) => handleEndDateChange(e, 0)} // Pass index to handle function if needed
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="startTime" className="block text-gray-600 mb-2">
                  Heure de début
                </label>
                <div className="flex items-center">
                  <input
                    type="time"
                    id="startTime"
                    name="startTime"
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                    value={eventData.start[0].time}
                    onChange={(e) => handleStartTimeChange(e, 0)}
                    required
                  />
                  <select
                    value={startAmPm}
                    onChange={handleStartAmPmChange}
                    className="ml-2 px-2 py-1 border rounded-md focus:outline-none focus:border-blue-500"
                  >
                    {ampmOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="endTime" className="block text-gray-600 mb-2">
                  Heure de fin
                </label>
                <div className="flex items-center">
                  <input
                    type="time"
                    id="endTime"
                    name="endTime"
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                    value={eventData.end[0].time}
                    onChange={(e) => handleEndTimeChange(e, 0)}
                    required
                  />
                  <select
                    value={endAmPm}
                    onChange={handleEndAmPmChange}
                    className="ml-2 px-2 py-1 border rounded-md focus:outline-none focus:border-blue-500"
                  >
                    {ampmOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <div className="mb-4" data-name="description">
                <label
                  htmlFor="description"
                  className="block mb-2 text-sm font-medium text-gray-900"
                >
                  Description
                </label>
                <Editor
                  value={eventData.description || ""}
                  onChange={handleDescriptionChange}
                  containerProps={{
                    className:
                      "add-new-post__editor mb-1 text-gray-900 bg-gray-50",
                    style: {
                      height: "400px", // Set your desired height
                      width: "800px", // Set your desired width
                    },
                  }}
                />
                <p className="text-xs text-gray-400">
                  Donnez une description complète. Pas plus de 3000 mots.
                </p>
              </div>

              <div className="mb-4">
                <select
                  id="eventType"
                  name="eventType"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                  value={eventData.eventType}
                  onChange={handleEventTypeChange}
                >
                  <option value="">Sélectionner le type d'événement</option>
                  <option value="Festival">Festival</option>
                  <option value="Conference">Conférence</option>
                  <option value="Seminar">Séminaire</option>
                  <option value="Executive Meeting">Réunion exécutive</option>
                  <option value="Webinar">Webinaire</option>
                  <option value="Comedy and Standup">
                    Comédie et stand-up
                  </option>
                  <option value="Musical Show">Spectacle musical</option>
                  <option value="Trade Fair">Foire commerciale</option>
                  <option value="Charity Events/Fundraisers">
                    Événements caritatifs/Collectes de fonds
                  </option>
                  <option value="Club Nights and Bars">
                    Soirées en club et bars
                  </option>
                  <option value="Concerts">Concerts</option>
                  <option value="Cultural Events">Événements culturels</option>
                  <option value="Trade Show">Salon professionnel</option>
                  <option value="Film Screenings">Projections de films</option>
                  <option value="Galas/Dinners">Galas/Dîners</option>
                  <option value="Gigs">Concerts (ou performances)</option>
                  <option value="Sports Events">Événements sportifs</option>
                  <option value="Theatre/Performing Arts">
                    Théâtre/Arts du spectacle
                  </option>
                  <option value="Workshops">Ateliers</option>
                  <option value="Others">Autres</option>
                </select>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div className="flex space-x-4 mb-4">
                <div className="flex-1" style={{ display: "none" }}>
                  <label
                    htmlFor="bearer"
                    className="block mb-2 text-sm font-medium text-white"
                  >
                    Porteur des frais
                  </label>
                  <select
                    id="bearer"
                    name="bearer"
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                    value={eventData.chargesBearer}
                    onChange={(e) =>
                      setEventData({
                        ...eventData,
                        chargesBearer: e.target.value,
                      })
                    }
                  >
                    <option value="">Sélectionner le porteur des frais</option>
                    <option value="client">Client</option>
                    <option value="moloyal">MoTickets</option>
                  </select>
                </div>

                <div className="flex-1">
                  <label
                    htmlFor="currency"
                    className="block mb-2 text-sm font-medium text-white"
                  >
                    Devise
                  </label>
                  <select
                    id="currency"
                    name="currency"
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                    value={eventData.currency}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                  >
                    <option value="">Sélectionner la devise</option>
                    <option value="GBP">GBP</option>
                    <option value="USD">USD</option>
                    <option value="NGN">NGN</option>
                    <option value="EUR">EUR</option>
                    <option value="MAD">MAD</option>
                  </select>
                </div>
              </div>

              <h3 className="text-xl font-semibold mb-4 text-white">
                Catégories de billets
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full mb-4">
                  <thead className="bg-[#25aae1]">
                    <tr>
                      <th className="px-4 py-2">Nom</th>
                      <th className="px-4 py-2">
                        Prix ({currencySymbolMap[eventData.currency]})
                      </th>
                      <th className="px-4 py-2" style={{ display: "none" }}>
                        Prix réduit(
                        {currencySymbolMap[eventData.currency]})
                      </th>

                      {/* <th className="px-4 py-2">Réduction portefeuille</th> */}
                      <th className="px-4 py-2">Qty</th>
                      <th className="px-4 py-2">Nombre de personnes</th>
                      <th className="px-4 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventData.ticketCategories.map((category, index) => (
                      <tr key={index}>
                        <td className="border px-4 py-2">
                          <input
                            type="text"
                            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                            value={category.name}
                            onChange={(e) =>
                              handleCategoryChange(
                                index,
                                "name",
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td className="border px-4 py-2">
                          <input
                            type="text"
                            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                            value={category.price}
                            onChange={(e) =>
                              handleCategoryChange(
                                index,
                                "price",
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td
                          className="border px-4 py-2"
                          style={{ display: "none" }}
                        >
                          <input
                            type="text"
                            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                            value={category.discountPrice}
                            onChange={(e) =>
                              handleCategoryChange(
                                index,
                                "discountPrice",
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td
                          className="border px-4 py-2"
                          style={{ display: "none" }}
                        >
                          <input
                            type="text"
                            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                            value={category.walletDiscount}
                            onChange={(e) =>
                              handleCategoryChange(
                                index,
                                "walletDiscount",
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td className="border px-4 py-2">
                          <input
                            type="text"
                            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                            value={category.qty}
                            onChange={(e) =>
                              handleCategoryChange(index, "qty", e.target.value)
                            }
                          />
                        </td>
                        <td className="border px-4 py-2">
                          <input
                            type="text"
                            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:border-blue-500"
                            value={category.numOfPeople}
                            onChange={(e) =>
                              handleCategoryChange(
                                index,
                                "numOfPeople",
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td className="border px-4 py-2">
                          <button
                            type="button"
                            className="text-red-500"
                            onClick={() => handleDeleteCategory(index)}
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                className="text-white bg-[#25aae1] px-4 py-2 rounded-md"
                onClick={handleAddCategory}
              >
                Ajouter une catégorie
              </button>
            </>
          )}

          <div className="mt-6 flex justify-between">
            {step !== 1 && (
              <button
                type="button"
                className="text-white bg-[#c10006] px-6 py-2 rounded-md"
                onClick={handlePreviousStep}
              >
                Précédent
              </button>
            )}
            {step !== 4 ? (
              <button
                type="button"
                className="text-white bg-[#25aae1] px-6 py-2 rounded-md disabled:opacity-70 disabled:cursor-not-allowed"
                onClick={handleNextStep}
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                className="text-white bg-[#25aae1] px-6 py-2 rounded-md disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Soumission en cours..." : "Soumettre"}
              </button>
            )}
          </div>
        </form>
        {submissionError && (
          <div className="text-red-500 mt-4">{submissionError}</div>
        )}
        <div className="flex justify-center items-center">
          <Link
            to="/dashboard"
            className="text-white bg-red-500 px-6 py-2 rounded-md"
          >
            Retourner au tableau de bord
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EditEventForm;
